import type {
  FormDetailDto,
  FormFieldDto,
  RegistrationFormAnswers,
  SubmissionListItemDto,
  TeamDetailDto,
} from '@napaicp/schemas'
import { ApiError, apiRequest } from '@/lib/api-client'
import { submissionKeys } from '@/features/submissions/query-keys'
import type { JudgeAssignment } from '@/features/judging/api'

/**
 * `/home` needs the same per-competition member data
 * `student-competition-page.tsx` already fetches (team/registration-form/
 * submissions) and `judge-assignments-page.tsx` already fetches (assignment
 * list), but across a *set* of competitions rather than one — which means
 * `useQueries` (an array of query configs), not calling those pages' own
 * hooks in a loop (not legal: hooks cannot be called a variable number of
 * times). These are plain query-option factories (no hook) so both a
 * single-competition hook (`features/home/api.ts`) and the aggregate
 * `useQueries` loop in `use-home-overview.ts` can share one definition.
 *
 * This intentionally duplicates `student-competition-page.tsx`'s private,
 * unexported team/registration-form queries rather than refactoring that
 * file to export them — this task's scope is `/home` only. Extracting a
 * single shared module both pages import from is a reasonable follow-up
 * cleanup, not done here.
 */

function nullOn404<T>(promise: Promise<T>): Promise<T | null> {
  return promise.catch((error: unknown) => {
    if (error instanceof ApiError && error.status === 404) return null
    throw error
  })
}

/** `null` means "no team yet" (404), not an error state — same as the student page's own `retry: false` team query. */
export function teamStatusQueryOptions(competitionId: string) {
  return {
    queryKey: ['home', competitionId, 'team'] as const,
    queryFn: () =>
      nullOn404(apiRequest<TeamDetailDto>(`/api/competitions/${competitionId}/teams/me`)),
    retry: false,
  }
}

export function registrationFormsQueryOptions(competitionId: string) {
  return {
    queryKey: ['home', competitionId, 'registration-forms'] as const,
    queryFn: () =>
      apiRequest<FormDetailDto[]>(
        `/api/competitions/${competitionId}/forms?type=registration_form`,
      ),
  }
}

/** `formId === undefined` disables the query — nothing to answer yet. */
export function registrationAnswersQueryOptions(competitionId: string, formId: string | undefined) {
  return {
    queryKey: ['home', competitionId, 'registration-form-answers', formId] as const,
    queryFn: () =>
      nullOn404(
        apiRequest<RegistrationFormAnswers>(
          `/api/competitions/${competitionId}/registration/forms/${formId}/answers`,
        ),
      ),
    enabled: formId !== undefined,
    retry: false,
  }
}

/** AC-373: the fields of the current published registration form version, to tell whether saved answers still satisfy it. Disabled until the form and its current version are known. */
export function registrationFieldsQueryOptions(
  competitionId: string,
  formId: string | undefined,
  versionId: string | undefined,
) {
  return {
    queryKey: ['home', competitionId, 'registration-form-fields', versionId] as const,
    queryFn: () =>
      apiRequest<FormFieldDto[]>(
        `/api/competitions/${competitionId}/forms/${formId}/versions/${versionId}/fields`,
      ),
    enabled: formId !== undefined && versionId !== undefined,
    retry: false,
  }
}

/** Shares its cache entry with `useMySubmissions` (`features/submissions/api.ts`) via the same query key. */
export function mySubmissionsQueryOptions(competitionId: string) {
  return {
    queryKey: submissionKeys.mine(competitionId),
    queryFn: () =>
      apiRequest<SubmissionListItemDto[]>(`/api/competitions/${competitionId}/submissions`),
  }
}

/** Shares its cache entry with `useJudgeAssignments` (`features/judging/api.ts`) via the same query key. */
export function judgeAssignmentsQueryOptions(competitionId: string) {
  return {
    queryKey: ['judge', competitionId, 'assignments'] as const,
    queryFn: () =>
      apiRequest<JudgeAssignment[]>(`/api/competitions/${competitionId}/judge/assignments`),
  }
}
